import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema, revisionSchema, text } from '../validators'
import { CLIENT_ACTION_STATUSES, CLIENT_ACTIONS } from '../vocab'

/**
 * Client actions (PORT-013..019): an investor's own decision about a company,
 * recorded for discussion. They never change a research finding, a holding or
 * send an instruction (PORT-015). Staff read them read-only (R-12).
 */

export const CLIENT_ACTION_NOTE_MAX = 2000

export const clientActionCreateSchema = z.strictObject({
  issuer_id: idSchema,
  engagement_id: idSchema.nullable().optional(),
  action: z.enum(CLIENT_ACTIONS.values),
  note: text(8, CLIENT_ACTION_NOTE_MAX),
})
export type ClientActionCreate = z.infer<typeof clientActionCreateSchema>

export const clientActionUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    action: z.enum(CLIENT_ACTIONS.values).optional(),
    note: text(8, CLIENT_ACTION_NOTE_MAX).optional(),
    status: z.enum(CLIENT_ACTION_STATUSES.values).optional(),
  })
  .refine((v) => v.action !== undefined || v.note !== undefined || v.status !== undefined, {
    message: 'Change the action, the note or the status.',
  })
export type ClientActionUpdate = z.infer<typeof clientActionUpdateSchema>

export const clientActionDeleteSchema = z.strictObject({ revision: revisionSchema })

export const clientActionListQuerySchema = pageQuerySchema.extend({
  issuer_id: idSchema.optional(),
  engagement_id: idSchema.optional(),
  /** Staff only: the client in context (R-06, R-12). */
  client_org_id: idSchema.optional(),
})
export type ClientActionListQuery = z.infer<typeof clientActionListQuerySchema>

const issuerRefSchema = z.object({ id: z.string(), name: z.string() })
const engagementRefSchema = z.object({ id: z.string(), title: z.string() })

/** Investor allowlist (P-ID-006): the owner's display name, never a user id. */
export const investorClientActionSchema = z.object({
  id: z.string(),
  issuer: issuerRefSchema,
  engagement: engagementRefSchema.nullable(),
  action: z.enum(CLIENT_ACTIONS.values),
  note: z.string(),
  status: z.enum(CLIENT_ACTION_STATUSES.values),
  owner_name: z.string(),
  mine: z.boolean(),
  external_delivery: z.literal(false),
  created_at: z.string(),
  updated_at: z.string(),
  revision: z.number().int(),
})
export type InvestorClientAction = z.infer<typeof investorClientActionSchema>

export const staffClientActionSchema = z.object({
  id: z.string(),
  org: z.object({ id: z.string(), name: z.string() }),
  issuer: issuerRefSchema,
  engagement: engagementRefSchema.nullable(),
  action: z.enum(CLIENT_ACTIONS.values),
  note: z.string(),
  status: z.enum(CLIENT_ACTION_STATUSES.values),
  owner: z.object({ id: z.string(), name: z.string() }),
  external_delivery: z.literal(false),
  created_at: z.string(),
  updated_at: z.string(),
})
export type StaffClientAction = z.infer<typeof staffClientActionSchema>
